import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Inbox, Loader2, Mail, MailOpen, RotateCcw, Search, Send, Trash2, User as UserIcon } from "lucide-react";
import {
    AdminSupportList,
    AdminSupportTicket,
    AdminSupportUser,
    deleteSupportTicket,
    getSupportTicket,
    getSupportTickets,
    replySupportTicket,
    supportErrorMessage,
    SupportStatus,
    updateSupportTicket,
} from "interfaces/support";
import { SUPPORT_AGENTS, supportAgent } from "components/support/agents";

/**
 * /bo/support — the tickets of the support chat widget (bottom right of the app). « Utilisateurs » menu.
 * Left: the tickets, waiting-for-us first; right: the conversation, the reply box and who wrote.
 * A reply shows in the client's widget and is emailed to them (unless unticked).
 */

const LIST_POLL_MS = 30_000;
// Last « Répondre en tant que » picked, kept for the next reply on this browser
const AGENT_KEY = "support_bo_agent";

const readAgent = () => {
    try {
        return supportAgent(localStorage.getItem(AGENT_KEY) ?? undefined).key;
    } catch {
        return SUPPORT_AGENTS[0].key;
    }
};

const TABS: { key: string; label: string; count?: keyof AdminSupportList["counts"] }[] = [
    { key: "open", label: "À traiter", count: "open" },
    { key: "unread", label: "Non lus", count: "unread" },
    { key: "answered", label: "Répondus", count: "answered" },
    { key: "closed", label: "Clôturés", count: "closed" },
    { key: "", label: "Tous" },
];

const STATUS_META: Record<SupportStatus, { label: string; className: string }> = {
    open: { label: "À traiter", className: "bg-orange-50 text-orange-700 border-orange-200" },
    answered: { label: "Répondu", className: "bg-green-50 text-green-700 border-green-200" },
    closed: { label: "Clôturé", className: "bg-gray-100 text-gray-600 border-gray-200" },
};

function StatusBadge({ status }: { status: SupportStatus }) {
    const meta = STATUS_META[status];
    return <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.className}`}>{meta.label}</span>;
}

function formatDate(d?: string, withTime = true) {
    if (!d) return "—";
    return new Date(d).toLocaleString("fr-FR", {
        timeZone: "Europe/Paris",
        day: "numeric",
        month: "short",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : { year: "numeric" }),
    });
}

function relative(d: string) {
    const minutes = Math.round((Date.now() - new Date(d).getTime()) / 60000);
    if (minutes < 1) return "à l'instant";
    if (minutes < 60) return `il y a ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.round(hours / 24);
    return days < 30 ? `il y a ${days} j` : formatDate(d, false);
}

function accountLabel(user: AdminSupportUser | null) {
    if (!user) return "Visiteur sans compte";
    if (user.isSub) return "Client premium";
    if (user.trialEndsAt) return new Date(user.trialEndsAt) < new Date() ? "Essai terminé" : `Essai jusqu'au ${formatDate(user.trialEndsAt, false)}`;
    return "Compte sans abonnement";
}

function TicketDetail({ id, onChanged, onDeleted }: { id: string; onChanged: () => void; onDeleted: () => void }) {
    const [data, setData] = useState<{ ticket: AdminSupportTicket; user: AdminSupportUser | null; otherTickets: { _id: string; subject: string; status: SupportStatus; lastMessageAt: string }[] } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [reply, setReply] = useState("");
    const [notifyByEmail, setNotifyByEmail] = useState(true);
    const [agent, setAgent] = useState(readAgent);
    const [sending, setSending] = useState(false);
    const [, setSearchParams] = useSearchParams();
    const endRef = useRef<HTMLDivElement>(null);

    const load = useCallback(async () => {
        try {
            setData(await getSupportTicket(id));
            setError(null);
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    }, [id]);

    useEffect(() => {
        setData(null);
        setReply("");
        load().then(onChanged);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // The client may write while the ticket is open
    useEffect(() => {
        const timer = setInterval(() => {
            if (document.visibilityState === "visible") load();
        }, LIST_POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [data?.ticket.messages.length]);

    const send = async (close = false) => {
        if (!reply.trim() || sending) return;
        setSending(true);
        setError(null);
        try {
            const ticket = await replySupportTicket(id, reply.trim(), { close, notifyByEmail, agent });
            setData((d) => (d ? { ...d, ticket } : d));
            setReply("");
            onChanged();
        } catch (e) {
            setError(supportErrorMessage(e));
        }
        setSending(false);
    };

    const update = async (fields: { status?: SupportStatus; unreadByAdmin?: boolean }) => {
        try {
            const ticket = await updateSupportTicket(id, fields);
            setData((d) => (d ? { ...d, ticket } : d));
            onChanged();
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    const remove = async () => {
        if (!window.confirm("Supprimer définitivement ce ticket et ses messages ?")) return;
        try {
            await deleteSupportTicket(id);
            onDeleted();
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    };

    if (!data) {
        return (
            <div className="flex flex-1 items-center justify-center text-sm text-[#68738d]">
                {error ?? <Loader2 className="h-5 w-5 animate-spin" />}
            </div>
        );
    }

    const { ticket, user, otherTickets } = data;
    const fullName = user ? [user.givenName, user.familyName].filter(Boolean).join(" ") : ticket.name;

    return (
        <div className="flex min-h-0 flex-1">
            {/* Conversation */}
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex flex-wrap items-center gap-3 border-b border-[#e6e9f0] px-5 py-4">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <h2 className="truncate text-base font-semibold text-[#17244a]">{ticket.subject}</h2>
                            <StatusBadge status={ticket.status} />
                        </div>
                        <p className="mt-0.5 text-xs text-[#68738d]">
                            Ouvert le {formatDate(ticket.createdAt)}
                            {ticket.status === "closed" && ticket.closedBy ? ` · clôturé par ${ticket.closedBy}` : ""}
                        </p>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => update({ unreadByAdmin: true })}
                            title="Marquer comme non lu"
                            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-[#273354] hover:bg-[#f5f7fb]"
                        >
                            <Mail className="h-4 w-4" />
                            Non lu
                        </button>
                        {ticket.status === "closed" ? (
                            <button
                                type="button"
                                onClick={() => update({ status: "open" })}
                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-[#273354] hover:bg-[#f5f7fb]"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Rouvrir
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => update({ status: "closed" })}
                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-[#273354] hover:bg-[#f5f7fb]"
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                Clôturer
                            </button>
                        )}
                        <button type="button" onClick={remove} title="Supprimer" className="rounded-lg p-1.5 text-[#7a849b] hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto bg-[#f5f7fb] px-5 py-5">
                    {ticket.messages.map((m) => {
                        const fromUs = m.from === "admin";
                        return (
                            <div key={m._id} className={`flex flex-col ${fromUs ? "items-end" : "items-start"}`}>
                                {fromUs ? (
                                    <span className="mb-1 flex items-center gap-1.5 text-[11px] text-[#68738d]" title={m.authorEmail ? `Envoyé par ${m.authorEmail}` : undefined}>
                                        <img src={supportAgent(m.agent).photo} alt="" className="h-4 w-4 rounded-full object-cover" />
                                        {supportAgent(m.agent).name} · {formatDate(m.createdAt)}
                                    </span>
                                ) : (
                                    <span className="mb-1 text-[11px] text-[#68738d]">
                                        {fullName || ticket.email} · {formatDate(m.createdAt)}
                                    </span>
                                )}
                                <div
                                    className={`max-w-[75%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                        fromUs ? "rounded-br-sm bg-[#1A1A1A] text-white" : "rounded-bl-sm border border-[#e6e9f0] bg-white text-[#17244a]"
                                    }`}
                                >
                                    {m.body}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={endRef} />
                </div>

                <div className="border-t border-[#e6e9f0] bg-white p-4">
                    {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <span className="mr-1 text-xs text-[#68738d]">Répondre en tant que</span>
                        {SUPPORT_AGENTS.map((a) => (
                            <button
                                key={a.key}
                                type="button"
                                onClick={() => {
                                    setAgent(a.key);
                                    try {
                                        localStorage.setItem(AGENT_KEY, a.key);
                                    } catch {
                                        // Not remembered: fine
                                    }
                                }}
                                aria-pressed={agent === a.key}
                                className={`inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2.5 text-xs transition-colors ${
                                    agent === a.key ? "border-[#1A1A1A] bg-[#1A1A1A] font-medium text-white" : "border-[#e6e9f0] text-[#273354] hover:bg-[#f5f7fb]"
                                }`}
                            >
                                <img src={a.photo} alt="" className="h-5 w-5 rounded-full object-cover" />
                                {a.name}
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                        }}
                        rows={4}
                        placeholder={`Répondre à ${fullName || ticket.email}…`}
                        className="w-full resize-y rounded-xl border border-[#e6e9f0] px-3 py-2 text-sm text-[#17244a] focus:border-[#17244a] focus:outline-none"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-2 text-xs text-[#68738d]">
                            <input type="checkbox" checked={notifyByEmail} onChange={(e) => setNotifyByEmail(e.target.checked)} />
                            Envoyer aussi par email à {ticket.email}
                        </label>
                        <span className="text-[11px] text-[#7a849b]">⌘ + Entrée pour envoyer</span>
                        <div className="ml-auto flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => send(true)}
                                disabled={!reply.trim() || sending}
                                className="rounded-lg border border-[#e6e9f0] px-3 py-1.5 text-xs font-medium text-[#273354] hover:bg-[#f5f7fb] disabled:opacity-50"
                            >
                                Envoyer et clôturer
                            </button>
                            <button
                                type="button"
                                onClick={() => send(false)}
                                disabled={!reply.trim() || sending}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A1A1A] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#333] disabled:opacity-50"
                            >
                                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                Envoyer
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Who wrote */}
            <aside className="hidden w-72 shrink-0 space-y-5 overflow-y-auto border-l border-[#e6e9f0] bg-white p-5 xl:block">
                <div>
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#f1f4fa] text-[#273b6b]">
                        <UserIcon className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-semibold text-[#17244a]">{fullName || "Sans nom"}</p>
                    <a href={`mailto:${ticket.email}`} className="break-all text-xs text-[#273b6b] hover:underline">
                        {ticket.email}
                    </a>
                    <p className="mt-2 text-xs text-[#68738d]">{accountLabel(user)}</p>
                </div>
                {user && (
                    <dl className="space-y-2 text-xs">
                        {[
                            ["Entreprise", user.company],
                            ["Téléphone", user.phone],
                            ["Inscrit le", formatDate(user.createdAt, false)],
                            ["Dernière connexion", user.lastConnection ? formatDate(user.lastConnection) : undefined],
                        ]
                            .filter(([, v]) => v)
                            .map(([k, v]) => (
                                <div key={k}>
                                    <dt className="text-[#7a849b]">{k}</dt>
                                    <dd className="text-[#17244a]">{v}</dd>
                                </div>
                            ))}
                    </dl>
                )}
                {ticket.page && (
                    <div className="text-xs">
                        <p className="text-[#7a849b]">Page d'ouverture</p>
                        <p className="break-all font-mono text-[#17244a]">{ticket.page}</p>
                    </div>
                )}
                {otherTickets.length > 0 && (
                    <div>
                        <p className="mb-2 text-xs font-semibold text-[#17244a]">Autres tickets</p>
                        <div className="space-y-1">
                            {otherTickets.map((t) => (
                                <button
                                    key={t._id}
                                    type="button"
                                    onClick={() => setSearchParams((p) => {
                                        p.set("ticket", t._id);
                                        return p;
                                    })}
                                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-[#f5f7fb]"
                                >
                                    <span className="min-w-0 flex-1 truncate text-[#273354]">{t.subject}</span>
                                    <StatusBadge status={t.status} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </aside>
        </div>
    );
}

export default function SupportTickets() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get("ticket");
    const status = searchParams.get("status") ?? "open";
    const [q, setQ] = useState("");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [list, setList] = useState<AdminSupportList | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setList(await getSupportTickets({ status, q: query || undefined, page }));
            setError(null);
        } catch (e) {
            setError(supportErrorMessage(e));
        }
    }, [status, query, page]);

    useEffect(() => {
        load();
        const timer = setInterval(() => {
            if (document.visibilityState === "visible") load();
        }, LIST_POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    useEffect(() => {
        const t = setTimeout(() => {
            setQuery(q.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    const setParam = (key: string, value: string | null) =>
        setSearchParams((p) => {
            if (value === null) p.delete(key);
            else p.set(key, value);
            return p;
        });

    return (
        <div className="flex h-[calc(100vh-69px)] flex-col bg-[#f5f7fb] px-5 py-6 md:px-8">
            <header className="mb-5 flex flex-wrap items-end gap-4">
                <div className="flex-1">
                    <h1 className="text-2xl font-semibold text-[#17244a]">Support</h1>
                    <p className="mt-0.5 text-sm text-[#68738d]">
                        Les messages envoyés depuis le chat en bas à droite de l'application. Chaque nouveau message est aussi posté sur Discord.
                    </p>
                </div>
                {list && (
                    <div className="flex gap-3 text-sm">
                        <span className="rounded-xl border border-[#e3e8f1] bg-white px-3 py-2 text-[#273354]">
                            <span className="font-semibold text-[#17244a]">{list.counts.open}</span> à traiter
                        </span>
                        <span className="rounded-xl border border-[#e3e8f1] bg-white px-3 py-2 text-[#273354]">
                            <span className="font-semibold text-[#17244a]">{list.counts.unread}</span> non lus
                        </span>
                    </div>
                )}
            </header>

            <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#e3e8f1] bg-white">
                {/* Tickets */}
                <div className="flex w-full max-w-sm shrink-0 flex-col border-r border-[#e6e9f0]">
                    <div className="space-y-3 border-b border-[#e6e9f0] p-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7a849b]" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Email, nom, sujet, message…"
                                className="w-full rounded-lg border border-[#e6e9f0] py-2 pl-9 pr-3 text-sm focus:border-[#17244a] focus:outline-none"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {TABS.map((tab) => {
                                const n = tab.count && list ? list.counts[tab.count] : undefined;
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => {
                                            setPage(1);
                                            setParam("status", tab.key);
                                        }}
                                        className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                                            status === tab.key ? "bg-[#1A1A1A] text-white" : "text-[#273354] hover:bg-[#f5f7fb]"
                                        }`}
                                    >
                                        {tab.label}
                                        {n !== undefined && n > 0 && <span className="ml-1 opacity-70">{n}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {error && <p className="p-4 text-xs text-red-600">{error}</p>}
                        {!list && !error && (
                            <div className="flex justify-center p-8">
                                <Loader2 className="h-5 w-5 animate-spin text-[#7a849b]" />
                            </div>
                        )}
                        {list && list.tickets.length === 0 && (
                            <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-[#68738d]">
                                <Inbox className="h-6 w-6 text-[#7a849b]" />
                                Aucun ticket ici.
                            </div>
                        )}
                        {list?.tickets.map((t) => (
                            <button
                                key={t._id}
                                type="button"
                                onClick={() => setParam("ticket", t._id)}
                                className={`flex w-full flex-col gap-1 border-b border-[#eef1f6] px-4 py-3 text-left transition-colors ${
                                    selectedId === t._id ? "bg-[#f5f7fb]" : "hover:bg-[#f9fafc]"
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    {t.unreadByAdmin ? (
                                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#e34d59]" aria-label="Non lu" />
                                    ) : (
                                        <MailOpen className="h-3.5 w-3.5 shrink-0 text-[#b3bacb]" />
                                    )}
                                    <span className={`min-w-0 flex-1 truncate text-sm ${t.unreadByAdmin ? "font-semibold text-[#17244a]" : "text-[#273354]"}`}>
                                        {t.name || t.email}
                                    </span>
                                    <span className="shrink-0 text-[11px] text-[#7a849b]">{relative(t.lastMessageAt)}</span>
                                </div>
                                <p className="truncate text-xs font-medium text-[#273354]">{t.subject}</p>
                                <p className="truncate text-xs text-[#68738d]">
                                    {t.lastMessage ? `${t.lastMessage.from === "admin" ? "Vous : " : ""}${t.lastMessage.body}` : ""}
                                </p>
                                <div className="flex items-center gap-1.5">
                                    <StatusBadge status={t.status} />
                                    {t.isSub && <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">Premium</span>}
                                    {!t.user && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">Visiteur</span>}
                                    {t.company && <span className="truncate text-[11px] text-[#7a849b]">{t.company}</span>}
                                </div>
                            </button>
                        ))}
                    </div>

                    {list && list.totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-[#e6e9f0] px-4 py-2 text-xs text-[#68738d]">
                            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40">
                                ← Précédent
                            </button>
                            <span>
                                {page} / {list.totalPages}
                            </span>
                            <button type="button" disabled={page >= list.totalPages} onClick={() => setPage(page + 1)} className="disabled:opacity-40">
                                Suivant →
                            </button>
                        </div>
                    )}
                </div>

                {/* Conversation */}
                {selectedId ? (
                    <TicketDetail key={selectedId} id={selectedId} onChanged={load} onDeleted={() => {
                        setParam("ticket", null);
                        load();
                    }} />
                ) : (
                    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-[#68738d]">
                        <Inbox className="h-8 w-8 text-[#b3bacb]" />
                        Sélectionnez un ticket.
                    </div>
                )}
            </div>
        </div>
    );
}
